import {
  CONTENT_LABEL_BUCKET_NAME,
  CONTENT_LABEL_NEEDS_DECISION_ID,
  CONTENT_LABEL_NEEDS_DECISION_NAME,
  CONTENT_LABEL_NULL_BUCKET_ID,
} from '../../utils/contentLabelBucket';
import type { LabelCategory } from '../settings/labels/types';

/** One removable chip in the Content tab's chip bar: "<category>: <value>". */
export type ContentLabelChip = {
  key: string;
  contentLabelCategoryId: string;
  /** A value id, or the `CONTENT_LABEL_NEEDS_DECISION_ID` / `CONTENT_LABEL_NULL_BUCKET_ID` sentinel. */
  valueId: string;
  label: string;
  value: string;
};

/** The rows after the values, in menu order: Need decision, then Unlabeled. */
const SENTINEL_ROWS = [
  { valueId: CONTENT_LABEL_NEEDS_DECISION_ID, value: CONTENT_LABEL_NEEDS_DECISION_NAME },
  { valueId: CONTENT_LABEL_NULL_BUCKET_ID, value: CONTENT_LABEL_BUCKET_NAME },
];

/**
 * One chip per selected row, read through the catalog: categories and their values in catalog order,
 * then Need decision, then Unlabeled, as the menu lists them. An id the catalog no longer has gets no chip.
 */
export function contentLabelChipsFor(
  selection: ReadonlyMap<string, ReadonlySet<string>>,
  categories: LabelCategory[],
): ContentLabelChip[] {
  return categories.flatMap((category) => {
    const picked = selection.get(category.labelCategoryId);
    if (!picked?.size) return [];

    const chipFor = (valueId: string, value: string): ContentLabelChip => ({
      key: `${category.labelCategoryId}:${valueId}`,
      contentLabelCategoryId: category.labelCategoryId,
      valueId,
      label: category.name,
      value,
    });

    const valueChips = category.labels
      .filter((label) => picked.has(label.labelId))
      .map((label) => chipFor(label.labelId, label.name));
    const sentinelChips = SENTINEL_ROWS.filter(({ valueId }) => picked.has(valueId)).map(({ valueId, value }) =>
      chipFor(valueId, value),
    );

    return [...valueChips, ...sentinelChips];
  });
}
